import type { ReactNode } from 'react';
import { Icon } from './Icon';

/** Label + control + help/error, as `.field` in the design. */
export function Field({
  id,
  label,
  help,
  error,
  was,
  children,
  style,
}: {
  id?: string;
  label: ReactNode;
  help?: ReactNode;
  error?: string | null;
  /** Previous saved value, shown under a changed field ("era 0,87"). */
  was?: string | null;
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className="field" style={style}>
      {id ? <label htmlFor={id}>{label}</label> : <span className="lbl">{label}</span>}
      {children}
      {error ? (
        <span className="help err">
          <Icon name="alert" size={14} />
          {error}
        </span>
      ) : was !== undefined && was !== null ? (
        <span className="help" style={{ color: 'var(--warn)' }}>
          era {was}
        </span>
      ) : help ? (
        <span className="help">{help}</span>
      ) : null}
    </div>
  );
}

/** Text input with optional prefix ("R$") and unit ("/kWh"), `.input` with `.qin` inside. */
export function AffixInput({
  id,
  value,
  onChange,
  prefix,
  unit,
  placeholder,
  disabled,
  changed,
  invalid,
  inputMode = 'decimal',
  ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  prefix?: string;
  unit?: string;
  placeholder?: string;
  disabled?: boolean;
  changed?: boolean;
  invalid?: boolean;
  inputMode?: 'decimal' | 'numeric' | 'text';
  ariaLabel?: string;
}) {
  const cls = ['input', changed ? 'changed' : '', invalid ? 'err' : '', disabled ? 'readonly' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      {prefix ? <span className="muted">{prefix}</span> : null}
      <input
        id={id}
        className="qin"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        inputMode={inputMode}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
      />
      {unit ? <span className="unit">{unit}</span> : null}
    </div>
  );
}

/** Segmented control (`.seg`). */
export function Seg<T extends string>({
  value,
  options,
  onChange,
  label,
  disabled,
}: {
  value: T | null;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <div
      className="seg"
      style={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}
      role="radiogroup"
      aria-label={label}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={value === o.value ? 'on' : ''}
          onClick={() => onChange(o.value)}
          disabled={disabled}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Dark "unsaved changes" bar (`.savebar`). */
export function SaveBar({
  label,
  detail,
  onDiscard,
  onSave,
  saveLabel = 'Salvar',
  saving,
}: {
  label: string;
  detail?: ReactNode;
  onDiscard: () => void;
  onSave: () => void;
  saveLabel?: string;
  saving?: boolean;
}) {
  return (
    <div className="savebar" role="region" aria-label="Alterações não salvas">
      <span className="grow">
        <b style={{ fontWeight: 600 }}>{label}</b> {detail}
      </span>
      <button
        type="button"
        className="btn sm"
        style={{ background: 'transparent', color: '#FFF6EC', borderColor: '#5A5A5A' }}
        onClick={onDiscard}
      >
        Descartar
      </button>
      <button type="button" className="btn sm c" onClick={onSave} disabled={saving}>
        {saving ? 'Salvando…' : saveLabel}
      </button>
    </div>
  );
}

export function Toggle({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={on ? 'toggle on' : 'toggle'}
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      disabled={disabled}
    />
  );
}

/** "Só Admin altera" note for the Operadora (design copy). */
export function ReadOnlyNote({ children }: { children: ReactNode }) {
  return (
    <div className="alert warn" role="note">
      <Icon name="lock" size={22} className="ic" />
      <div>{children}</div>
    </div>
  );
}

/** A labelled on/off switch (the design's "check" row with a toggle). */
export function SwitchRow({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      className="check sm"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
    >
      <span className={on ? 'toggle on' : 'toggle'} aria-hidden="true" />
      {label}
    </button>
  );
}
